import React, { useState } from 'react';
import { PageHeader, ScrollReveal, CTASection } from '../components/UI';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqCategories = [
    {
      title: 'Getting Started',
      faqs: [
        {
          q: 'How do I get started with Crescit Marketing?',
          a: 'It all starts with a free strategy call. We\'ll discuss your business, goals, and current marketing situation. From there, we\'ll determine if we\'re a good fit and outline next steps. There\'s no pressure and no obligation—just a straightforward conversation about how we can help.'
        },
        {
          q: 'What information do you need to get started?',
          a: 'We\'ll need access to your website, Google Analytics (if you have it), Google Ads account (if applicable), and your Google Business Profile. We\'ll also want to understand your target customers, service areas, and business goals. Don\'t worry—we\'ll guide you through everything.'
        },
        {
          q: 'How long does onboarding take?',
          a: 'Our onboarding process typically takes 1-2 weeks. This includes the initial audit, strategy development, and getting all accounts and tracking set up properly. We move quickly but thoroughly—we want to start generating results as soon as possible without cutting corners.'
        },
      ]
    },
    {
      title: 'Results & Timeline',
      faqs: [
        {
          q: 'How quickly will I see results?',
          a: 'It depends on the service. Google Ads can start generating leads within the first week once campaigns are live. SEO is a longer-term investment—you\'ll typically start seeing meaningful improvements in rankings and traffic within 3-4 months, with significant results in 6-12 months. We set realistic expectations upfront and provide monthly progress reports.'
        },
        {
          q: 'What kind of results can I expect?',
          a: 'While every business is different, our clients typically see: 200-400% increase in organic traffic, 30-50% reduction in cost-per-lead, first page rankings for target keywords, and significant increases in qualified leads. We\'ll provide specific projections based on your market and competition during our strategy call.'
        },
        {
          q: 'Do you guarantee results?',
          a: 'We guarantee our commitment to your success, but we can\'t guarantee specific rankings or lead numbers—anyone who does is lying to you. What we can guarantee is complete transparency, proven methodologies, and a relentless focus on improving your results month over month. If you\'re not seeing progress, we\'ll adjust strategy until you do.'
        },
      ]
    },
    {
      title: 'Pricing & Contracts',
      faqs: [
        {
          q: 'How much do your services cost?',
          a: 'Our pricing is customized based on your specific needs, market competition, and goals. Most clients invest between $1,500-$5,000 per month for our services. We offer packages for Google Ads management, Local SEO, Technical SEO, and comprehensive digital marketing. Every engagement starts with a free consultation where we\'ll provide a detailed proposal with transparent pricing—no hidden fees, ever.'
        },
        {
          q: 'Do you require long-term contracts?',
          a: 'No. We believe in earning your business every month. While SEO requires time to show results (and we recommend at least a 6-month commitment to see the full impact), we don\'t lock you into lengthy contracts. Our clients stay because they see results, not because they\'re contractually obligated. We do require 30 days notice to cancel.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept credit cards, ACH bank transfers, and wire transfers. Payment is due at the beginning of each month. For Google Ads clients, ad spend is billed separately directly by Google.'
        },
      ]
    },
    {
      title: 'Services',
      faqs: [
        {
          q: 'What industries do you specialize in?',
          a: 'We specialize in home services (roofing, HVAC, plumbing, electrical, landscaping), healthcare (dental, medical practices, clinics), professional services (lawyers, accountants, consultants), and local service businesses. Our strategies are battle-tested in these industries with proven track records of success. That said, our core methodology works across most B2C and local B2B businesses.'
        },
        {
          q: 'Do you offer one-time services or only monthly retainers?',
          a: 'We primarily work on monthly retainer engagements because that\'s what drives the best results. However, we do offer one-time audits and consulting for businesses that want to handle implementation themselves. These start at $500 for a basic audit and go up from there depending on scope.'
        },
        {
          q: 'Can you work with my existing marketing team or agency?',
          a: 'Absolutely. We often complement in-house teams or work alongside other agencies. We\'re happy to focus on specific areas (like SEO while you handle social media) or provide strategic oversight for your team\'s execution. We\'re collaborative, not territorial.'
        },
      ]
    },
    {
      title: 'Communication & Reporting',
      faqs: [
        {
          q: 'How often will we communicate?',
          a: 'During the implementation phase (first 8 weeks), you\'ll receive weekly email updates and bi-weekly strategy calls. After that, we move to monthly reporting and monthly strategy calls. Of course, you can reach your account manager anytime via email or phone—we typically respond within a few hours during business days.'
        },
        {
          q: 'What kind of reporting do you provide?',
          a: 'You\'ll receive access to a real-time dashboard showing key metrics, plus detailed monthly reports that explain: what we did, what happened, what we learned, and what we\'re doing next. Our reports focus on metrics that matter to your business—leads, conversions, revenue impact—not vanity metrics like impressions.'
        },
        {
          q: 'Who will I be working with?',
          a: 'You\'ll have a dedicated account manager who is your single point of contact. They\'re responsible for strategy, communication, and ensuring results. Behind them is our team of specialists who handle execution. You\'ll always know who to call, and you\'ll never be passed around to different people.'
        },
      ]
    },
    {
      title: 'Technical Questions',
      faqs: [
        {
          q: 'Do I need to give you access to my website?',
          a: 'For SEO services, yes—we\'ll need either direct access to make changes or we\'ll provide detailed instructions for your web developer. For Google Ads only, we just need access to your Google Ads account. We\'re always careful with access and follow security best practices.'
        },
        {
          q: 'What if I don\'t have a website yet?',
          a: 'We can help with that too. We partner with trusted web developers who can build conversion-optimized websites for local businesses. Alternatively, we can recommend website builders if you\'re on a tight budget. A good website is essential for marketing success, so we\'ll figure out the best solution for your situation.'
        },
        {
          q: 'Will SEO changes break my website?',
          a: 'No. We\'re extremely careful with technical SEO changes and always test before deploying. Major changes are done incrementally so we can monitor for any issues. We also maintain documentation of all changes so anything can be reverted if needed. In years of doing this, we\'ve never broken a client\'s website.'
        },
      ]
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about working with us. Can't find your answer? Just ask—we're always happy to help."
        breadcrumb="FAQ"
      />

      {/* FAQ Sections */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {faqCategories.map((category, catIndex) => (
            <ScrollReveal key={catIndex} delay={catIndex * 0.1}>
              <div style={{ marginBottom: 48 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 20, paddingBottom: 10, borderBottom: '2px solid #ff6b35' }}>
                  {category.title}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {category.faqs.map((faq, faqIndex) => {
                    const globalIndex = `${catIndex}-${faqIndex}`;
                    const isActive = activeIndex === globalIndex;
                    return (
                      <div 
                        key={faqIndex}
                        style={{
                          background: 'white',
                          borderRadius: 12,
                          overflow: 'hidden',
                          border: isActive ? '2px solid #ff6b35' : '1px solid #eee',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div 
                          onClick={() => setActiveIndex(isActive ? null : globalIndex)}
                          style={{
                            padding: '20px 24px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            background: isActive ? 'rgba(255,107,53,0.05)' : 'white'
                          }}
                        >
                          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', paddingRight: 20, margin: 0 }}>
                            {faq.q}
                          </h3>
                          <span style={{
                            fontSize: 24,
                            color: '#ff6b35',
                            transform: isActive ? 'rotate(45deg)' : 'rotate(0)',
                            transition: 'transform 0.3s ease',
                            flexShrink: 0
                          }}>
                            +
                          </span>
                        </div>
                        <div style={{
                          maxHeight: isActive ? 500 : 0,
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease'
                        }}>
                          <p style={{
                            padding: '0 24px 24px',
                            color: '#666',
                            lineHeight: 1.8,
                            fontSize: 15,
                            margin: 0
                          }}>
                            {faq.a}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <ScrollReveal>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginBottom: 16 }}>
              Still Have Questions?
            </h2>
            <p style={{ color: '#666', fontSize: 17, lineHeight: 1.7, marginBottom: 32 }}>
              We're always happy to chat. Reach out anytime and we'll get back to you within a few hours.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📧</div>
                <div style={{ color: '#888', fontSize: 14, marginBottom: 4 }}>Email Us</div>
                <a href="mailto:hello@crescitmarketing.com" style={{ color: '#1a1a2e', fontWeight: 600, fontSize: 16 }}>
                  hello@crescitmarketing.com
                </a>
              </div>
              <div>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📱</div>
                <div style={{ color: '#888', fontSize: 14, marginBottom: 4 }}>Call Us</div>
                <a href="tel:+15551234567" style={{ color: '#1a1a2e', fontWeight: 600, fontSize: 16 }}>
                  +1 (555) 123-4567
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default FAQ;
